import React, { useMemo } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, Link, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Close as CloseIcon, PersonOutline as PersonIcon } from '@mui/icons-material';
import { formatDate, formatPartnerSource, getSituationChipSx, isValidValue } from '../../../helpers/PartnerHelper';
import { partnerTableColors, partnerTableSx } from '../Parterns/partnerListTheme';

const valueOrDash = (value) => isValidValue(value) ? value : '—';

const Info = ({ label, value, secondary }) => <Box>
  <Typography sx={{ color: '#667085', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{label}</Typography>
  <Typography sx={{ color: '#101828', fontSize: 14, fontWeight: 600, mt: 0.25, overflowWrap: 'anywhere' }}>{valueOrDash(value)}</Typography>
  {secondary && <Typography sx={{ color: '#667085', fontSize: 11, mt: 0.2 }}>{secondary}</Typography>}
</Box>;

const PersonalDetailsDialog = ({ open, person, rows, onClose, onPartnerOpen }) => {
  const summary = useMemo(() => {
    if (!person || !rows.length) return null;
    const firstValue = (field) => person[field] || rows.find((row) => isValidValue(row[field]))?.[field] || '';
    return {
      name: firstValue('nomPersonnel'),
      personId: firstValue('personId'),
      cin: firstValue('cin'),
      telephone: firstValue('telephonePersonnel'),
      email: firstValue('emailPersonnel'),
      adresse: firstValue('adresse'),
      dateNaissance: firstValue('dateNaissance'),
      diplome: firstValue('diplome'),
      filiere: firstValue('filiere'),
      functions: [...new Set(rows.map((row) => row.fonction).filter(isValidValue))],
    };
  }, [person, rows]);

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
    <DialogTitle sx={{ bgcolor: partnerTableColors.dark, color: '#fff', p: 2.2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ bgcolor: 'rgba(255,255,255,.14)', borderRadius: '50%', display: 'grid', height: 44, placeItems: 'center', width: 44 }}><PersonIcon /></Box>
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 800, opacity: 0.75 }}>FICHE PERSONNEL</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{summary?.name || 'Personnel'}</Typography>
            {summary?.personId && <Typography sx={{ fontSize: 11, opacity: 0.8 }}>{summary.personId}</Typography>}
          </Box>
        </Stack>
        <Tooltip title="Fermer"><IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton></Tooltip>
      </Stack>
    </DialogTitle>

    <DialogContent sx={{ bgcolor: partnerTableColors.soft, p: { xs: 1.5, md: 2.5 } }}>
      {!summary ? <Typography color="text.secondary">Aucune donnée disponible.</Typography> : <Stack spacing={2}>
        <Paper variant="outlined" sx={{ borderColor: partnerTableColors.border, borderRadius: 2, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><Info label="CIN" value={summary.cin} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Info label="Téléphone" value={summary.telephone} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Info label="Email" value={summary.email} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Info label="Date de naissance" value={summary.dateNaissance ? formatDate(summary.dateNaissance) : ''} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Info label="Diplôme" value={summary.diplome} secondary={summary.filiere} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Info label="Fonction(s)" value={summary.functions.join(', ')} /></Grid>
            <Grid item xs={12} md={6}><Info label="Adresse" value={summary.adresse} /></Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ borderColor: partnerTableColors.border, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ borderBottom: `1px solid ${partnerTableColors.border}`, px: 2, py: 1.5 }}>
            <Typography sx={{ color: partnerTableColors.dark, fontSize: 15, fontWeight: 800 }}>Affectations et contrats associés</Typography>
            <Typography sx={{ color: '#667085', fontSize: 11 }}>{rows.length} affectation(s)</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 390 }}><Table stickyHeader size="small" sx={partnerTableSx}>
            <TableHead><TableRow>
              <TableCell>Partenaire</TableCell><TableCell>N° marché</TableCell><TableCell>Fonction</TableCell>
              <TableCell>Situation personnel</TableCell><TableCell>Début personnel</TableCell><TableCell>Fin personnel</TableCell>
              <TableCell>Cause rupture</TableCell><TableCell>Situation contrat</TableCell><TableCell>Base source</TableCell>
            </TableRow></TableHead>
            <TableBody>{rows.map((row) => <TableRow key={`affectation-${row.affectationPersonnelId || row.id}`} hover>
              <TableCell>{row.partenaire && row.partenaire !== '—' ? <Box><Link component="button" type="button" underline="hover" onClick={() => onPartnerOpen(row)} sx={{ color: partnerTableColors.dark, display: 'block', fontSize: 12, fontWeight: 700, textAlign: 'left' }}>{row.partenaire}</Link><Typography sx={{ color: '#667085', fontSize: 10, mt: 0.25 }}>{valueOrDash(row.partnerId)}</Typography></Box> : '—'}</TableCell>
              <TableCell>{valueOrDash(row.numeroMarche)}</TableCell>
              <TableCell>{valueOrDash(row.fonction)}</TableCell>
              <TableCell>{valueOrDash(row.situationPersonnel)}</TableCell>
              <TableCell>{formatDate(row.dateDebutContratPersonnel)}</TableCell>
              <TableCell>{formatDate(row.dateFinContratPersonnel)}</TableCell>
              <TableCell>{valueOrDash(row.causeRupturePersonnel)}</TableCell>
              <TableCell><Chip size="small" label={valueOrDash(row.situation)} sx={getSituationChipSx(row.situation)} /></TableCell>
              <TableCell>{formatPartnerSource(row.source_db)}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>
        </Paper>
      </Stack>}
    </DialogContent>
    <DialogActions sx={{ borderTop: `1px solid ${partnerTableColors.border}`, px: 2.5, py: 1.5 }}>
      <Button onClick={onClose} variant="contained" sx={{ bgcolor: partnerTableColors.dark, '&:hover': { bgcolor: '#102e49' } }}>Fermer</Button>
    </DialogActions>
  </Dialog>;
};

export default PersonalDetailsDialog;
